
import React, { useState } from "react";
import Main from "../pages/Main";
import { useDispatch, useSelector } from "react-redux";
import { updateMarchantPassword } from "../api/userAuth";
import { updateUserInfo } from "../features/authSlice";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../context/NotifyContext";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { AppBreadcrumbs } from "../components/listing";
import ConfirmDialog from "../components/ui/ConfirmDialog";

const Password = () => {
  const [oldPass, setOldPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [remark, setRemark] = useState("");
  const [loading, setLoading] = useState(false);

  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { userInfo } = useSelector((state) => state?.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { notify } = useNotify();

  const handleChangePassword = (e) => {
    e.preventDefault();

    if (!oldPass.trim()) {
      return notify("Old Password is required", "error");
    }

    if (!newPass.trim()) {
      return notify("New Password is required", "error");
    }

    if (!confirmPass.trim()) {
      return notify("Confirm Password is required", "error");
    }

    if (newPass !== confirmPass) {
      return notify("New & Confirm password mismatch", "error");
    }

    if (!remark.trim()) {
      return notify("Remarks are required", "error");
    }

    if (!userInfo?._id) {
      return notify("User not found", "error");
    }

    if (oldPass === newPass) {
      return notify(
        "New password must be different from the old password",
        "error"
      );
    }

    setConfirmOpen(true);
  };

  const confirmChangePassword = async () => {
    if (loading) return;

    setLoading(true);

    try {
      const payload = {
        oldPass: oldPass.trim(),
        newPass: newPass.trim(),
        remarks: remark.trim(),
      };

      const data = await updateMarchantPassword(userInfo._id, payload);

      if (data?.success) {
        const response = data?.data?.user;

        if (response) {
          dispatch(
            updateUserInfo({
              name: response?.name,
            })
          );
        }

        setConfirmOpen(false);

        setOldPass("");
        setNewPass("");
        setConfirmPass("");
        setRemark("");

        notify(
          data?.message || "Password changed successfully!",
          "success"
        );

        navigate("/", { replace: true });
      } else {
        notify(data?.message || "Failed to change password", "error");
      }
    } catch {
      notify("Server error. Please try again", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Main showRightPanel={false} showInfoText={false}>
      <div className="mt-4">
        <AppBreadcrumbs
          items={[
            { label: "Dashboard", to: "/" },
            { label: "Profile" },
            { label: "Change Password" },
          ]}
        />

        <div className="ds-card mb-4 flex items-center justify-between">
          <p className="text-[15px] font-semibold text-[var(--text-dark)]">
            Change Password
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
          <form
            onSubmit={handleChangePassword}
            className="ds-card w-full max-w-md"
          >
            <h3 className="text-[15px] font-semibold mb-4 text-[var(--text-dark)]">
              Change Password
            </h3>

            <label className="ds-label">
              Old Password{" "}
              <span className="text-[var(--error-color)]">*</span>
            </label>

            <div className="relative mb-4">
              <input
                type={showOld ? "text" : "password"}
                placeholder="Enter Old Password"
                value={oldPass}
                onChange={(e) => setOldPass(e.target.value.trimStart())}
                className="ds-input pr-10"
                autoComplete="current-password"
              />

              <button
                type="button"
                onClick={() => setShowOld((prev) => !prev)}
                aria-label={showOld ? "Hide old password" : "Show old password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-[var(--text-muted)]"
              >
                {showOld ? (
                  <VisibilityOff fontSize="small" />
                ) : (
                  <Visibility fontSize="small" />
                )}
              </button>
            </div>

            <label className="ds-label">
              New Password{" "}
              <span className="text-[var(--error-color)]">*</span>
            </label>

            <div className="relative mb-4">
              <input
                type={showNew ? "text" : "password"}
                placeholder="Create New Password"
                value={newPass}
                onChange={(e) => setNewPass(e.target.value.trimStart())}
                className="ds-input pr-10"
                autoComplete="new-password"
              />

              <button
                type="button"
                onClick={() => setShowNew((prev) => !prev)}
                aria-label={showNew ? "Hide new password" : "Show new password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-[var(--text-muted)]"
              >
                {showNew ? (
                  <VisibilityOff fontSize="small" />
                ) : (
                  <Visibility fontSize="small" />
                )}
              </button>
            </div>

            <label className="ds-label">
              Confirm Password{" "}
              <span className="text-[var(--error-color)]">*</span>
            </label>

            <div className="relative mb-4">
              <input
                type={showConfirm ? "text" : "password"}
                placeholder="Confirm Your New Password"
                value={confirmPass}
                onChange={(e) => setConfirmPass(e.target.value.trimStart())}
                className="ds-input pr-10"
                autoComplete="new-password"
              />

              <button
                type="button"
                onClick={() => setShowConfirm((prev) => !prev)}
                aria-label={
                  showConfirm ? "Hide confirm password" : "Show confirm password"
                }
                className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-[var(--text-muted)]"
              >
                {showConfirm ? (
                  <VisibilityOff fontSize="small" />
                ) : (
                  <Visibility fontSize="small" />
                )}
              </button>
            </div>

            <label className="ds-label">
              Remarks <span className="text-[var(--error-color)]">*</span>
            </label>

            <textarea
              rows={3}
              placeholder="Your reason for password change"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              className="ds-input mb-4 resize-none"
            />

            <div className="ds-form-actions">
              <button
                type="submit"
                disabled={loading}
                className="ds-btn ds-btn-primary"
              >
                Change Password
              </button>

              <button
                type="button"
                onClick={() => navigate("/")}
                disabled={loading}
                className="ds-btn ds-btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Confirm Password Change"
        message="Are you sure you want to change your password?"
        confirmLabel="Change Password"
        confirmColor="primary"
        loading={loading}
        onCancel={() => {
          if (!loading) setConfirmOpen(false);
        }}
        onConfirm={confirmChangePassword}
      />
    </Main>
  );
};

export default Password;
